import "./index.css";
import * as ReactDOM from "react-dom/client";
import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import App from "./App";

import { AppProvider } from "./contextProviders/AppContext";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import {
  defaultPrimaryColors,
  defaultSecondaryColors,
} from "./components/themes/defaultColors";
import {
  gnistaPrimaryColors,
  gnistaSecondaryColors,
} from "./components/themes/customColors";
import { disableSentry } from "./features";

const DISABLE_SENTRY = disableSentry();
const FILTERED_SENTRY_DOMAIN = "sahkopyorat.net";

if (!DISABLE_SENTRY) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || "unknown",
    autoSessionTracking: true,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.5,
    denyUrls: [
      /maps\.googleapis\.com\/maps\/api\/js/i,
      /maps\.googleapis\.com\/maps-api-v3\/api\/js/i,
      /service\.giosg\.com\/live2/i,
    ],
    beforeSend: (event, hint) => {
      console.error(hint.originalException || hint.syntheticException);
      if (
        process.env.REACT_APP_ENV === "local" ||
        process.env.REACT_APP_ENV === "dev"
      ) {
        return null;
      }
      const error = hint.originalException;
      if (error && error.message) {
        if (
          error.message.match(
            /OnDisconnect.update failed: First argument contains undefined in property/i // validateFirebaseData, issue example: 3523903360
          )
        ) {
          return null;
        }
      }
      const eventUrl = event.request?.url;
      if (eventUrl?.includes(FILTERED_SENTRY_DOMAIN)) {
        return null;
      }

      const exceptionValues = event.exception?.values;
      if (exceptionValues) {
        const stackFrames = exceptionValues.flatMap(
          (value) => value.stacktrace?.frames || []
        );

        const isFilteredDomainError = stackFrames.some((frame) =>
          frame.filename?.includes(FILTERED_SENTRY_DOMAIN)
        );
        if (isFilteredDomainError) {
          return null;
        }

        const isWordPressError = stackFrames.some(
          (frame) =>
            frame.filename.includes("wp-content") ||
            frame.filename.includes("wp-includes")
        );

        if (isWordPressError) {
          // Handle or log the WordPress-related error
          console.log("WordPress-related error detected:", event);
          return null; // Drop the event
        }
      }

      return event;
    },
  });
  Sentry.setTag("app", "customer-v2");
}

const container = document.querySelector("#urent-app-root");
const shadowContainer = container.attachShadow({ mode: "open" });
const emotionRoot = document.createElement("style");
const shadowRootElement = document.createElement("div");
shadowContainer.appendChild(emotionRoot);
shadowContainer.appendChild(shadowRootElement);

const cache = createCache({
  key: "css",
  prepend: true,
  container: emotionRoot,
});

const groups = window.URENT_APP_GROUPS;
const orgId = window.URENT_APP_ORG_ID;
const selectedPrimaryColors = () => {
  //if (!groups) return defaultPrimaryColors;

  if (groups && groups[0] === "gnista") return gnistaPrimaryColors;
  return defaultPrimaryColors;
};
const selectedSecondaryColors = () => {
  if (groups && groups[0] === "gnista") return gnistaSecondaryColors;
  return defaultSecondaryColors;
};

const shadowTheme = createTheme({
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      "Roboto",
      "Sofia Pro",
      "sans-serif",
    ].join(","),
    h1: {
      fontSize: "6rem",
    },

    h2: {
      fontSize: "3.75rem",
    },

    h3: {
      fontSize: "3rem",
    },

    h4: {
      fontSize: "2.125rem",
    },

    h5: {
      fontSize: 18,
      fontWeight: "bolder",
      fontFamily: "Sofia Pro, sans serif",
      marginBottom: 20,
      letterSpacing: 0.5,
    },
    h6: {
      fontSize: 27,

      fontWeight: "bold",
      fontFamily: "Sofia Pro, sans serif",
      letterSpacing: 1,
    },

    subtitle1: {
      fontSize: 16,
    },

    subtitle2: {
      fontSize: 11,
      fontFamily: "Roboto",
    },
    body1: {
      fontSize: 14,
      fontFamily: "Roboto",
    },
    body2: {
      fontSize: 14,
      fontFamily: "Sofia Pro, sans serif",
    },

    button: {
      fontSize: 16,
      textTransform: "none",
    },
    caption: {
      fontSize: 14,
    },
    overline: {
      fontSize: 14,
    },
  },
  palette: {
    primary: selectedPrimaryColors(),
    secondary: selectedSecondaryColors(),
    error: {
      main: "#aa0000",
    },
    selected: {
      main: "#6aff59",
    },
  },

  components: {
    MuiPopover: {
      defaultProps: {
        container: shadowRootElement,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: shadowRootElement,
      },
    },
    MuiModal: {
      defaultProps: {
        container: shadowRootElement,
      },
    },
  },
});

ReactDOM.createRoot(shadowRootElement).render(
  <CacheProvider value={cache}>
    <AppProvider>
      <ThemeProvider theme={shadowTheme}>
        <App />
      </ThemeProvider>
    </AppProvider>
  </CacheProvider>
);
